<template>
  <div>
    <NuxtLayout name="default">
      <article class="news-article pt-space-md pt-md-0">
        <hero-article-atlas
          :title="data?.newsArticle?.title!"
          :bg-image-source="data?.newsArticle?.coverImage ?? undefined"
          :posted-by="data?.newsArticle?.author?.name ?? ''"
          :minutes-read="
            data?.newsArticle?.timeToRead
              ? parseInt(data?.newsArticle?.timeToRead)
              : 0
          "
          :article-date="formatDate(data?.newsArticle?.updatedDate)"
          :show-breadcrumbs="true"
          class="bg-light-3"
          :breadcrumbs="breadcrumbItems"
          :show-social-media="false"
          @onBreadcrumbClick="handleBreadcrumbClick"
        >
          <template #body>
            <div
              v-if="data?.newsArticle?.shortDescription"
              class="fs-xl mb-space-lg fw-bold"
              v-html="data?.newsArticle?.shortDescription"
            ></div>
            <div
              class="d-flex flex-row flex-nowrap justify-content-left align-items-center"
            >
              <span class="fs-large fw-bold me-space-xs">Share:</span>
              <NuxtLink
                v-for="(item, index) in socialMediaItems"
                :key="index"
                :class="{
                  'me-space-xs': index != socialMediaItems.length - 1,
                }"
                :to="generateSocialLinkURL(item)"
                :title="item.ariaText"
                :aria-label="item.ariaText"
                target="_blank"
                external
                @click.native="
                  handleSocialMediaLinkClick(
                    data?.newsArticle?.title as string,
                    item,
                    data?.newsArticle?.detailPage!
                  )
                "
              >
                <font-awesome-icon
                  :icon="item.icon"
                  :aria-label="item.ariaText"
                  class="text-dark-3 social-media-icon"
                />
              </NuxtLink>
            </div>
          </template>
        </hero-article-atlas>

        <!-- first video section -->
        <div v-if="data?.newsArticle?.firstVideo">
          <div class="container">
            <div class="row">
              <div class="col-12 col-lg-10 offset-lg-1">
                <div class="mb-space-lg mb-lg-space-xl">
                  <video-caption-apollo
                    :bg-image-source="data?.newsArticle?.firstVideo.imageSource!"
                    :caption="
                      data?.newsArticle?.firstVideo.captionText ?? undefined
                    "
                    :video-id="data?.newsArticle?.firstVideo.youtubeId!"
                    @vcaModalOpen="
                      handleVideoModalEvent(
                        'open',
                        data?.newsArticle?.title!,
                        'play button'
                      )
                    "
                    @vcaModalClose="
                      handleVideoModalEvent(
                        'close',
                        data?.newsArticle?.title!,
                        'close button'
                      )
                    "
                  ></video-caption-apollo>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- first content section -->
        <div v-if="data?.newsArticle?.firstContent">
          <div class="container">
            <div class="row">
              <div class="col-12 col-lg-8 offset-lg-2">
                <div class="mb-space-lg mb-lg-space-xl">
                  <h2
                    v-if="data?.newsArticle?.firstContent.title"
                    class="h2-medium mb-space-sm"
                  >
                    {{ data?.newsArticle?.firstContent.title }}
                  </h2>
                  <div
                    class="content-body-text"
                    v-html="data?.newsArticle?.firstContent.description"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- first image section -->
        <div v-if="data?.newsArticle?.firstImage">
          <div class="container">
            <div class="row">
              <div
                v-if="data?.newsArticle?.firstImage.isPortrait"
                class="col-12 col-lg-6 mx-auto"
              >
                <div class="mb-space-lg mb-lg-space-xl">
                  <image-portrait-atlas
                    :image-source="data?.newsArticle?.firstImage.image!"
                    :image-alt="data?.newsArticle?.firstImage.imageAlt!"
                    :caption="data?.newsArticle?.firstImage.caption!"
                  ></image-portrait-atlas>
                </div>
              </div>
              <div v-else class="col-12 col-md-12 mx-auto px-0 px-md-gutter">
                <div class="mb-space-lg mb-lg-space-xl mx-0 mx-md-space-md">
                  <image-caption-apollo
                    :image-source="data?.newsArticle?.firstImage.image!"
                    :image-alt="data?.newsArticle?.firstImage.imageAlt!"
                    :caption="data?.newsArticle?.firstImage.caption!"
                  ></image-caption-apollo>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- first infographic section -->
        <div v-if="data?.newsArticle?.firstInfographic">
          <div class="container">
            <div class="row">
              <div class="col-12 col-md-6 offset-md-3">
                <div class="mb-space-lg mb-lg-space-xl">
                  <image-infographic-atlas
                    :image-source="data?.newsArticle?.firstInfographic.image!"
                    :image-alt="data?.newsArticle?.firstInfographic.imageAlt!"
                    :cta-text="data?.newsArticle?.firstInfographic.ctaText!"
                    @modalOpen="
                      handleModalEvent(
                        'open',
                        data?.newsArticle?.title!,
                        data?.newsArticle?.firstInfographic?.ctaText!
                      )
                    "
                    @modalClose="
                      handleModalEvent(
                        'close',
                        data?.newsArticle?.title!,
                        'close cross'
                      )
                    "
                    @buttonClick="
                      handleLinkEvent(data?.newsArticle?.title!, 'back to page')
                    "
                  ></image-infographic-atlas>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- second content section -->
        <div v-if="data?.newsArticle?.secondContent">
          <div class="container">
            <div class="row">
              <div class="col-12 col-lg-8 offset-lg-2">
                <div class="mb-space-lg mb-lg-space-xl">
                  <h2
                    v-if="data?.newsArticle?.secondContent.title"
                    class="h2-medium mb-space-sm"
                  >
                    {{ data?.newsArticle?.secondContent.title }}
                  </h2>
                  <div
                    v-if="data?.newsArticle?.secondContent.description"
                    class="content-body-text"
                    v-html="data?.newsArticle?.secondContent.description"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- first carousel section -->
        <div
          v-if="data?.newsArticle?.imageCarousel && data?.newsArticle?.imageCarousel!.length > 0"
        >
          <div class="container">
            <div class="row">
              <div class="col-12">
                <div class="mb-space-lg mb-lg-space-xl">
                  <div
                    class="d-flex d-lg-none justify-content-center align-items-center"
                  >
                    <carousel-apollo
                      :per-slide="1"
                      :items="(data?.newsArticle?.imageCarousel as ParagraphImageApollo[])"
                      @previousSlideClick="
                        handleSlideClick(
                          data?.newsArticle?.title!,
                          'left chevron'
                        )
                      "
                      @nextSlideClick="
                        handleSlideClick(
                          data?.newsArticle?.title!,
                          'right chevron'
                        )
                      "
                    >
                      <template v-slot:default="slotProps">
                        <div
                          class="cia-div mb-space-lg"
                          v-for="(item, index) in slotProps.paginatedItems"
                          :key="index"
                        >
                          <div
                            :data-background-image="item.image"
                            class="cia-image mb-space-sm"
                          >
                            <span class="sr-only">{{ item.imageAlt }}</span>
                          </div>
                          <div
                            v-if="item.caption"
                            class="text-dark-1 fs-small mb-space-sm"
                            v-html="item.caption"
                          ></div>
                          <div class="bg-secondary cia-line"></div>
                        </div>
                      </template>
                    </carousel-apollo>
                  </div>

                  <div class="d-none d-lg-block">
                    <carousel-atlas
                      :items="(data?.newsArticle?.imageCarousel as ParagraphImageApollo[])"
                      @previousSlideClick="
                        handleSlideClick(
                          data?.newsArticle?.title!,
                          'left chevron'
                        )
                      "
                      @nextSlideClick="
                        handleSlideClick(
                          data?.newsArticle?.title!,
                          'right chevron'
                        )
                      "
                    >
                      <template v-slot:default="slotProps">
                        <div
                          class="cia-div mb-space-lg"
                          v-for="(item, index) in slotProps.paginatedItems"
                          :key="index"
                        >
                          <div
                            :data-background-image="item.image"
                            class="cia-image mb-space-sm"
                          >
                            <span class="sr-only">{{ item.imageAlt }}</span>
                          </div>
                          <div
                            v-if="item.caption"
                            class="text-dark-1 fs-small mb-space-sm"
                            v-html="item.caption"
                          ></div>
                          <div class="bg-secondary cia-line"></div>
                        </div>
                      </template>
                    </carousel-atlas>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- second video section -->
        <div v-if="data?.newsArticle?.secondVideo">
          <div class="container">
            <div class="row">
              <div class="col-12 col-lg-10 offset-lg-1">
                <div class="mb-space-lg mb-lg-space-xl">
                  <video-caption-apollo
                    :bg-image-source="data?.newsArticle?.secondVideo.imageSource!"
                    :caption="data?.newsArticle?.secondVideo.captionText!"
                    :video-id="data?.newsArticle?.secondVideo.youtubeId!"
                    class="mb-space-sm"
                    @vcaModalOpen="
                      handleVideoModalEvent(
                        'open',
                        data?.newsArticle?.title!,
                        'play button'
                      )
                    "
                    @vcaModalClose="
                      handleVideoModalEvent(
                        'close',
                        data?.newsArticle?.title!,
                        'close button'
                      )
                    "
                  ></video-caption-apollo>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- third content section -->
        <div v-if="data?.newsArticle?.thirdContent">
          <div class="container">
            <div class="row">
              <div class="col-12 col-lg-8 offset-lg-2">
                <div class="mb-space-lg mb-lg-space-xl">
                  <h2
                    v-if="data?.newsArticle?.thirdContent.title"
                    class="h2-medium mb-space-sm"
                  >
                    {{ data?.newsArticle?.thirdContent.title }}
                  </h2>
                  <div
                    class="content-body-text"
                    v-html="data?.newsArticle?.thirdContent.description"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- first testimonial section -->
        <div v-if="data?.newsArticle?.firstTestimonial">
          <div class="container">
            <div class="row">
              <div class="col-12 col-lg-8 offset-lg-2">
                <div class="mb-space-lg mb-lg-space-xl">
                  <testimonial-apollo
                    :title="data?.newsArticle?.firstTestimonial.quote!"
                    :author="data?.newsArticle?.firstTestimonial.authorName!"
                    :designation="data?.newsArticle?.firstTestimonial.authorDesignation!"
                  ></testimonial-apollo>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- first list section -->
        <div
          v-if="data?.newsArticle?.listItems && data?.newsArticle?.listItems!.length > 0"
        >
          <div class="container">
            <div class="row">
              <div class="col-12 col-lg-6 offset-lg-3">
                <div class="mb-space-lg mb-lg-space-xl">
                  <div
                    v-for="(item, index) in data?.newsArticle?.listItems"
                    :key="index"
                  >
                    <list-item-apollo
                      class="mb-space-md"
                      :title="item!.title!"
                      :text="item!.description!"
                    ></list-item-apollo>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- first card info section -->
        <section v-if="data?.newsArticle?.firstCardInformation">
          <div class="container">
            <div class="row">
              <div class="col-12 col-lg-8 offset-lg-2">
                <div class="mb-space-lg mb-lg-space-xl">
                  <!-- firstCardInformation.htmlLinks; verified as an Array in script -->
                  <card-info-horizontal
                    v-if="
                      data?.newsArticle?.firstCardInformation.htmlLinks
                        ?.length == 0
                    "
                    border-variant="light-4"
                    :bg-image-source="data?.newsArticle?.firstCardInformation.image!"
                    :highlight-width="8"
                    :display-label="false"
                    title-size="large"
                    :title="data?.newsArticle?.firstCardInformation.title!"
                    :display-cta="false"
                  >
                    <div
                      class="mb-space-md"
                      v-html="data?.newsArticle?.firstCardInformation.text"
                    ></div>
                  </card-info-horizontal>
                  <card-info-horizontal
                    v-else-if="
                      data?.newsArticle?.firstCardInformation.htmlLinks?.[0]
                    "
                    border-variant="light-4"
                    :bg-image-source="data?.newsArticle?.firstCardInformation.image!"
                    :highlight-width="8"
                    :display-label="false"
                    title-size="large"
                    :title="data?.newsArticle?.firstCardInformation.title!"
                    :cta-as-button="true"
                    cta-button-variant="secondary"
                    cta-text-variant="dark-3"
                    cta-text-size="small"
                    cta-text-weight="bold"
                    :cta-link="(data?.newsArticle?.firstCardInformation.htmlLinks![0]!.uri! as string)"
                    :cta-link-target="formatLinkTarget(data?.newsArticle?.firstCardInformation.htmlLinks![0]!.target!)"
                    :cta-text="data?.newsArticle?.firstCardInformation.htmlLinks![0]!.text!"
                    @ctaClick="
                      handleCardInformationCtaClick(
                        $event,
                        data?.newsArticle?.firstCardInformation?.title!,
                        data?.newsArticle?.firstCardInformation?.htmlLinks![0]!
                          .text!
                      )
                    "
                  >
                    <div
                      class="mb-space-md"
                      v-html="data?.newsArticle?.firstCardInformation.text"
                    ></div>
                  </card-info-horizontal>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- fourth content section -->
        <div v-if="data?.newsArticle?.fourthContent">
          <div class="container">
            <div class="row">
              <div class="col-12 col-lg-8 offset-lg-2">
                <div class="mb-space-lg mb-lg-space-xl">
                  <h2
                    v-if="data?.newsArticle?.fourthContent.title"
                    class="h2-medium mb-space-sm"
                  >
                    {{ data?.newsArticle?.fourthContent.title }}
                  </h2>
                  <div
                    class="content-body-text"
                    v-html="data?.newsArticle?.fourthContent.description"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- second image section -->
        <div v-if="data?.newsArticle?.secondImage">
          <div class="container">
            <div class="row">
              <div
                v-if="data?.newsArticle?.secondImage.isPortrait"
                class="col-12 col-lg-6 mx-auto"
              >
                <div class="mb-space-lg mb-lg-space-xl">
                  <image-portrait-atlas
                    :image-source="data?.newsArticle?.secondImage.image!"
                    :image-alt="data?.newsArticle?.secondImage.imageAlt!"
                    :caption="
                      data?.newsArticle?.secondImage.caption ?? undefined
                    "
                  ></image-portrait-atlas>
                </div>
              </div>
              <div v-else class="col-12 col-md-12 mx-auto px-0 px-md-gutter">
                <div class="mb-space-lg mb-lg-space-xl mx-0 mx-md-space-md">
                  <image-caption-apollo
                    :image-source="data?.newsArticle?.secondImage.image!"
                    :image-alt="data?.newsArticle?.secondImage.imageAlt!"
                    :caption="
                      data?.newsArticle?.secondImage.caption ?? undefined
                    "
                  ></image-caption-apollo>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- second infographic section -->
        <div v-if="data?.newsArticle?.secondInfographic">
          <div class="container">
            <div class="row">
              <div class="col-12 col-md-6 offset-md-3">
                <div class="mb-space-lg mb-lg-space-xl">
                  <image-infographic-atlas
                    :image-source="data?.newsArticle?.secondInfographic.image!"
                    :image-alt="data?.newsArticle?.secondInfographic.imageAlt!"
                    :cta-text="data?.newsArticle?.secondInfographic.ctaText!"
                    @modalOpen="
                      handleModalEvent(
                        'open',
                        data?.newsArticle?.title!,
                        data?.newsArticle?.secondInfographic?.ctaText!
                      )
                    "
                    @modalClose="
                      handleModalEvent(
                        'close',
                        data?.newsArticle?.title!,
                        'close cross'
                      )
                    "
                    @buttonClick="
                      handleLinkEvent(data?.newsArticle?.title!, 'back to page')
                    "
                  ></image-infographic-atlas>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- second testimonial section -->
        <div v-if="data?.newsArticle?.secondTestimonial">
          <div class="container">
            <div class="row">
              <div class="col-12 col-lg-8 offset-lg-2">
                <div class="mb-space-lg mb-lg-space-xl">
                  <testimonial-apollo
                    :title="data?.newsArticle?.secondTestimonial.quote!"
                    :author="data?.newsArticle?.secondTestimonial.authorName!"
                    :designation="data?.newsArticle?.secondTestimonial.authorDesignation!"
                  ></testimonial-apollo>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- fifth content section -->
        <div v-if="data?.newsArticle?.fifthContent">
          <div class="container">
            <div class="row">
              <div class="col-12 col-lg-8 offset-lg-2">
                <div class="mb-space-lg mb-lg-space-xl">
                  <h2
                    v-if="data?.newsArticle?.fifthContent.title"
                    class="h2-medium mb-space-sm"
                  >
                    {{ data?.newsArticle?.fifthContent.title }}
                  </h2>
                  <div
                    class="content-body-text"
                    v-html="data?.newsArticle?.fifthContent.description"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- second card info section -->
        <section
          v-if="
            data?.newsArticle?.secondCardInformation &&
            data?.newsArticle?.secondCardInformation.htmlLinks?.length
          "
        >
          <div class="container">
            <div class="row">
              <div class="col-12 col-lg-8 offset-lg-2">
                <div class="mb-space-lg mb-lg-space-xl">
                  <card-info-horizontal
                    v-if="
                      data?.newsArticle?.secondCardInformation?.htmlLinks
                        ?.length == 0
                    "
                    border-variant="light-4"
                    :bg-image-source="data?.newsArticle?.secondCardInformation?.image!"
                    :highlight-width="8"
                    :display-label="false"
                    title-size="large"
                    :title="data?.newsArticle?.secondCardInformation?.title!"
                    :display-cta="false"
                  >
                    <div
                      class="mb-space-md"
                      v-html="data?.newsArticle?.secondCardInformation?.text"
                    ></div>
                  </card-info-horizontal>
                  <card-info-horizontal
                    v-else-if="
                      data?.newsArticle?.secondCardInformation &&
                      data?.newsArticle?.secondCardInformation.htmlLinks &&
                      data?.newsArticle?.secondCardInformation.htmlLinks[0]
                    "
                    border-variant="light-4"
                    :bg-image-source="data?.newsArticle?.secondCardInformation.image!"
                    :highlight-width="8"
                    :display-label="false"
                    title-size="large"
                    :title="data?.newsArticle?.secondCardInformation.title!"
                    :cta-as-button="true"
                    cta-button-variant="secondary"
                    cta-text-variant="dark-3"
                    cta-text-size="small"
                    cta-text-weight="bold"
                    :cta-link="data?.newsArticle?.secondCardInformation?.htmlLinks[0].uri!"
                    :cta-link-target="formatLinkTarget(data?.newsArticle?.secondCardInformation.htmlLinks[0].target!)"
                    :cta-text="data?.newsArticle?.secondCardInformation?.htmlLinks[0].text!"
                    @ctaClick="
                      handleCardInformationCtaClick(
                        $event,
                        data?.newsArticle?.secondCardInformation?.title!,
                        data?.newsArticle?.secondCardInformation?.htmlLinks![0]!
                          .text!
                      )
                    "
                  >
                    <div
                      class="mb-space-md"
                      v-html="data?.newsArticle?.secondCardInformation?.text"
                    ></div>
                  </card-info-horizontal>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- sixth content section -->
        <div v-if="data?.newsArticle?.sixthContent">
          <div class="container">
            <div class="row">
              <div class="col-12 col-lg-8 offset-lg-2">
                <div class="mb-space-lg mb-lg-space-xl">
                  <h2
                    v-if="data?.newsArticle?.sixthContent.title"
                    class="h2-medium mb-space-sm"
                  >
                    {{ data?.newsArticle?.sixthContent.title }}
                  </h2>
                  <div
                    class="content-body-text"
                    v-html="data?.newsArticle?.sixthContent.description"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- third image section -->
        <div v-if="data?.newsArticle?.thirdImage">
          <div class="container">
            <div class="row">
              <div
                v-if="data?.newsArticle?.thirdImage.isPortrait"
                class="col-12 col-lg-6 mx-auto"
              >
                <div class="mb-space-lg mb-lg-space-xl">
                  <image-portrait-atlas
                    :image-source="data?.newsArticle?.thirdImage?.image!"
                    :image-alt="data?.newsArticle?.thirdImage?.imageAlt!"
                    :caption="
                      data?.newsArticle?.thirdImage.caption ?? undefined
                    "
                  ></image-portrait-atlas>
                </div>
              </div>
              <div v-else class="col-12 col-md-12 mx-auto px-0 px-md-gutter">
                <div class="mb-space-lg mb-lg-space-xl mx-0 mx-md-space-md">
                  <image-caption-apollo
                    :image-source="data?.newsArticle?.thirdImage.image!"
                    :image-alt="data?.newsArticle?.thirdImage.imageAlt!"
                    :caption="
                      data?.newsArticle?.thirdImage.caption ?? undefined
                    "
                  ></image-caption-apollo>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- seventh content section -->
        <div v-if="data?.newsArticle?.seventhContent">
          <div class="container">
            <div class="row">
              <div class="col-12 col-lg-8 offset-lg-2">
                <div class="mb-space-lg mb-lg-space-xl">
                  <h2
                    v-if="data?.newsArticle?.seventhContent.title"
                    class="h2-medium mb-space-sm"
                  >
                    {{ data?.newsArticle?.seventhContent.title }}
                  </h2>
                  <div
                    class="content-body-text"
                    v-html="data?.newsArticle?.seventhContent.description"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- social media section -->
        <div>
          <div class="container">
            <div class="row">
              <div class="col-12 col-lg-8 offset-lg-2">
                <div class="mb-space-lg mb-lg-space-xxl">
                  <hr class="mt-0" />
                  <div
                    class="d-flex flex-row flex-nowrap justify-content-center align-items-center py-space-md"
                  >
                    <span class="fs-large fw-bold me-space-xs">Share:</span>
                    <a
                      v-for="(item, index) in socialMediaItems"
                      :key="index"
                      :class="{
                        'me-space-xs': index != socialMediaItems.length - 1,
                      }"
                      :href="generateSocialLinkURL(item)"
                      rel="nofollow"
                      :title="item.ariaText"
                      :aria-label="item.ariaText"
                      target="_blank"
                      @click="
                        handleSocialMediaLinkClick(
                          data?.newsArticle?.title as string,
                          item,
                          data?.newsArticle?.detailPage!
                        )
                      "
                    >
                      <font-awesome-icon
                        :icon="item.icon"
                        :aria-label="item.ariaText"
                        class="text-dark-3 social-media-icon"
                      />
                    </a>
                  </div>
                  <hr />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- author section -->
        <footer>
          <section>
            <div class="container">
              <div class="row">
                <div class="col-12 col-lg-8 offset-lg-2">
                  <div
                    class="mb-space-lg mb-lg-space-xxl p-space-sm p-lg-space-lg bg-light-2 border border-light-4"
                  >
                    <h3 class="h3-large mb-space-sm">
                      {{ data?.newsArticle?.authorInformationTitle }}
                    </h3>
                    <div class="fs-large">
                      <div
                        class="mb-space-sm"
                        v-html="data?.newsArticle?.author!.bio!"
                      ></div>
                      <div
                        v-if="data?.newsArticle?.additionalAuthorInformation"
                        class="bg-secondary line mb-space-sm"
                      ></div>
                      <div
                        v-if="data?.newsArticle?.additionalAuthorInformation"
                        v-html="data?.newsArticle?.additionalAuthorInformation"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </footer>
      </article>

      <section
        v-if="
          data &&
          data?.newsArticle?.relatedArticles &&
          data?.newsArticle?.relatedArticles.length >= 1
        "
        class="px-space-xs py-space-md px-md-space-md py-md-space-lg px-lg-space-xl py-lg-space-xxl position-relative bg-image rds-lazy"
        data-background-image="https://live-asuocms.ws.asu.edu/sites/default/files/UnityUI-NetworkPattern-White%402x.png"
      >
        <div class="container">
          <div class="row">
            <div class="col-12">
              <h2 class="h2-medium mb-space-sm">
                {{ data?.newsArticle?.relatedArticlesTitle }}
              </h2>
              <div class="bg-secondary title-underline mb-space-md"></div>
            </div>
          </div>
          <div class="row row-cols-1 row-cols-md-2 row-cols-lg-4 mb-space-lg">
            <div
              class="col mb-space-sm"
              v-for="(item, index) in data?.newsArticle?.relatedArticles"
              :key="index"
            >
              <card-image-article
                class="h-100"
                :clickable-card="true"
                border-variant="light-4"
                :image-source="item!.coverImage!"
                :image-alt="item!.title!"
                :title="item!.title!"
                :date="formatDate(item!.updatedDate!)"
                :minutes="item!.timeToRead!"
                cta-text="Read more"
                :cta-link="item!.detailPage!"
                cta-text-weight="bold"
                @cardClick="
                  handleCardArticleClick(
                    $event,
                    data?.newsArticle?.relatedArticlesTitle!,
                    item!.title!,
                    'card - article',
                    item!.detailPage!
                  )
                "
                @ctaClick="
                  handleCardArticleClick(
                    $event,
                    data?.newsArticle?.relatedArticlesTitle!,
                    item!.title!,
                    'card - article',
                    item!.detailPage!
                  )
                "
                @cardEnter="handleEnterKeyEvent(item!.detailPage!)"
              >
              </card-image-article>
            </div>
          </div>
          <div class="row text-center">
            <div class="col-12">
              <NuxtLink
                href="/newsroom"
                role="button"
                class="border-0 bg-dark-3 text-white fw-bold px-space-md py-space-xs"
                @click="
                  handleButtonClick(
                    data?.newsArticle?.relatedArticlesTitle!,
                    'Back to newsroom'
                  )
                "
              >
                Back to newsroom
              </NuxtLink>
            </div>
          </div>
        </div>
      </section>

      <section-rfi-multi-step :bg-image="RfiBgImage"></section-rfi-multi-step>
    </NuxtLayout>
  </div>
</template>

<script setup lang="ts">
// Types
import {
  NewsArticle,
  ParagraphCardInformation,
  ParagraphImageApollo,
  ParagraphInfographicAtlas,
  ParagraphVideoApollo,
} from "~/server/resolvers/resolvers-types";

// RDS Components
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import {
  HeroArticleAtlas,
  type BreadCrumb,
} from "@rds-vue-ui/hero-article-atlas";

// Custom Components
import CarouselApollo from "~/components/CarouselApollo.vue";
import CarouselAtlas from "~/components/CarouselAtlas.vue";
import ImageCaptionApollo from "~/components/newsroom/ImageCaptionApollo.vue";
import ListItemApollo from "~/components/newsroom/ListItemApollo.vue";
import TestimonialApollo from "~/components/newsroom/TestimonialApollo.vue";

// RFI resources
import { Maybe } from "graphql/jsutils/Maybe";
import RfiBgImage from "~/assets/img/rfi-bg-overlay.png";
import SectionRfiMultiStep from "~/components/rfi/SectionRfiMultiStep.vue";

// composables
const route = useRoute();
const config = useRuntimeConfig();
const UtilityService = useUtilityService();
const SchemaService = useSchemaService();

// page query setup
interface NewsArticlePageQuery {
  newsArticle: Maybe<NewsArticle>;
}

const articlesQuery = `#graphql
  query($slug: String!){
    newsArticle(slug: $slug){
      id
      detailPage
      metaTitle
      metaDescription
      newsCategory {
        title
      }
      contentHub {
        title
      }
      coverImage
      title
      publishedDate
      updatedDate
      timeToRead
      shortDescription
      firstVideo {
        captionText
        duration
        imageSource
        youtubeId
      }
      firstContent {
        title
        description
      }
      firstImage {
        image
        imageAlt
        caption
        isPortrait
      }
      firstInfographic {
        image
        imageAlt
        ctaText
      }
      secondContent {
        title
        description
      }
      imageCarousel {
        image
        imageAlt
        caption
      }
      secondVideo {
        captionText
        duration
        imageSource
        youtubeId
      }
      thirdContent {
        title
        description
      }
      firstTestimonial {
        quote
        authorName
        authorDesignation
      }
      listItems {
        title
        description
      }
      firstCardInformation {
        image
        title
        text
        htmlLinks {
          text
          uri
          target
        }
      }
      fourthContent {
        title
        description
      }
      secondImage {
        image
        imageAlt
        caption
        isPortrait
      }
      secondInfographic {
        image
        imageAlt
        ctaText
      }
      secondTestimonial {
        quote
        authorName
        authorDesignation
      }
      fifthContent {
        title
        description
      }
      secondCardInformation {
        image
        title
        text
        htmlLinks {
          text
          uri
          target
        }
      }
      sixthContent {
        title
        description
      }
      thirdImage {
        image
        imageAlt
        caption
        isPortrait
      }
      seventhContent {
        title description
      }
      authorInformationTitle
      author{
        name
        bio
      }
      additionalAuthorInformation
      relatedArticlesTitle
      relatedArticles {
        detailPage
        coverImage
        title
        updatedDate
        timeToRead
      }
    }
  }`,
  articlesQueryVariables = {
    slug: route.params.article as string,
  };

// query for this page's data
const data = await usePageQuery<NewsArticlePageQuery>(
  articlesQuery,
  articlesQueryVariables,
  {
    key: `news-article-${route.params.article as string}`,
  }
);

// require this page's data
if (!(data && data.newsArticle)) {
  throw createError({
    message: `${String(useRoute().name)} query failed`,
  });
}

// ensure consumed newsArticle data is present
if (isRequiredArticleDataPresent(data?.newsArticle) === false) {
  throw createError({ message: "Missing required data for Page." });
}

// page metadata setup
definePageMeta({
  layout: false,
  __dangerouslyDisableSanitizersByTagID: {
    "ldjson-schema": ["innerHTML"],
  },
});

// <head> setup

useHead({
  title: data?.newsArticle?.metaTitle,
  meta: [
    { name: "Title", content: data?.newsArticle?.metaTitle },
    { name: "Description", content: data?.newsArticle?.metaDescription },
    ...UtilityService.generateSocialTags(
      data?.newsArticle?.metaTitle!,
      data?.newsArticle?.metaDescription!,
      data?.newsArticle?.coverImage!
    ),
  ],
  link: [
    {
      rel: "canonical",
      href: UtilityService.formatCanonicalUrl(
        config.public.siteUrl + data?.newsArticle?.detailPage
      ),
    },
  ],
  script: [SchemaService.generateNewsArticleSchema(data?.newsArticle)],
});

const breadcrumbItems = ref<BreadCrumb[]>([
  {
    link: "/newsroom/",
    target: "_self",
    text: "Newsroom",
  },
  {
    link: data?.newsArticle?.contentHub?.title!,
    target: "_self",
    text: data?.newsArticle?.newsCategory?.title!,
  },
  {
    link: data?.newsArticle?.detailPage ? data?.newsArticle?.detailPage : "",
    target: "_self",
    text: data?.newsArticle?.title ? data?.newsArticle?.title : "",
  },
]);

// set up social media items
interface SocialMediaItem {
  title: string;
  icon: string | string[];
  ariaText: string;
}

const socialMediaItems: SocialMediaItem[] = [
  {
    title: "facebook",
    icon: ["fab", "facebook-square"],
    ariaText: "Facebook Icon",
  },
  {
    title: "twitter",
    icon: ["fab", "twitter-square"],
    ariaText: "Twitter Icon",
  },
  {
    title: "linkedin",
    icon: ["fab", "linkedin"],
    ariaText: "Linkedin Icon",
  },
  {
    title: "mail",
    icon: "envelope-square",
    ariaText: "Mail Icon",
  },
];

// internal functions

function formatDate(date: string | undefined | null) {
  if (date) {
    return UtilityService.formatDate(date);
  } else {
    return "";
  }
}

const formatLinkTarget = (
  target: string
): "_blank" | "_self" | "_parent" | "_top" | undefined => {
  if (target === "BLANK" || target === "_blank") {
    return "_blank";
  } else if (target === "SELF") {
    return "_self";
  } else if (target === "PARENT") {
    return "_parent";
  } else if (target === "TOP") {
    return "_top";
  } else {
    return "_self";
  }
};

function handleBreadcrumbClick(event: Event, item: BreadCrumb) {
  if (!data?.newsArticle?.title) {
    throw createError({
      message: "Missing required data?.newsArticle?.title for event tracking.",
    });
  }

  event.preventDefault();
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    data?.newsArticle?.title ? data?.newsArticle?.title?.toLowerCase() : "",
    item.text.toLowerCase()
  );

  navigateTo(item.link);
}

function handleVideoModalEvent(action: string, section: string, text: string) {
  analyticsComposable.trackModalEvent(
    "onclick",
    action,
    "click",
    "main content",
    section.toLowerCase(),
    text,
    "video - modal"
  );
}

function handleSlideClick(section: string, text: string) {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "click",
    "carousel",
    "main content",
    section.toLowerCase(),
    text,
    "carousel - image"
  );
}

function handleCardInformationCtaClick(
  event: Event,
  section: string,
  ctaText: string
) {
  const eventTarget = event.target;

  if (!(eventTarget instanceof HTMLElement)) {
    throw createError({
      message: "Event Target not an instance of HTMLElement.",
    });
  }

  event.preventDefault();

  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    section.toLowerCase(),
    ctaText.toLowerCase(),
    "card-info-horizontal"
  );

  const linkHref = eventTarget.getAttribute("href");
  const linkTarget = eventTarget.getAttribute("target");

  if (!linkHref) {
    throw createError({ message: "Link href required." });
  }
  navigateTo(linkHref, {
    open: {
      target: linkTarget === "_self" ? "_self" : "_blank",
    },
  });
}

function handleSocialMediaLinkClick(
  title: string,
  item: SocialMediaItem,
  path: string
) {
  analyticsComposable.trackLinkEvent(
    "external link",
    "main content",
    title.toLowerCase(),
    item.title.toLowerCase()
  );
}

function generateSocialLinkURL(item: SocialMediaItem) {
  if (!data?.newsArticle?.detailPage || !data?.newsArticle?.title) {
    throw createError({
      message: "Missing required newsArticle property for event tracking.",
    });
  }

  const shareLink = UtilityService.generateSocialShareLink(
    item.title,
    data?.newsArticle?.detailPage,
    data?.newsArticle?.title
  );

  return shareLink;
}

function handleCardArticleClick(
  event: Event,
  section: string,
  text: string,
  component: string,
  ctaLink: string
) {
  event.preventDefault();

  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    section.toLowerCase(),
    text.toLowerCase(),
    component.toLowerCase()
  );

  window.location.href = ctaLink;
}

function handleButtonClick(section: string, text: string) {
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    section.toLowerCase(),
    text.toLowerCase()
  );
}

function handleEnterKeyEvent(link: string) {
  window.location.href = link;
}

function handleModalEvent(action: string, section: string, text: string) {
  analyticsComposable.trackModalEvent(
    "onclick",
    action,
    "click",
    "main content",
    section.toLowerCase(),
    text.toLowerCase()
  );
}

function handleLinkEvent(section: string, text: string) {
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    section.toLowerCase(),
    text.toLowerCase()
  );
}

function isRequiredArticleDataPresent(newsArticle: NewsArticle | undefined) {
  // check for primary data
  if (
    !(
      typeof newsArticle?.metaTitle === "string" &&
      typeof newsArticle?.metaDescription === "string" &&
      typeof newsArticle?.detailPage === "string" &&
      typeof newsArticle?.title === "string" &&
      typeof newsArticle?.coverImage === "string" &&
      typeof newsArticle?.author === "object" &&
      typeof newsArticle?.timeToRead === "string" &&
      Array.isArray(newsArticle?.imageCarousel) &&
      Array.isArray(newsArticle?.listItems)
    )
  ) {
    console.error(
      "Missing primary data in News Article " + route.params.article
    );
    // return false;
  }

  // check any related articles data
  if (
    data?.newsArticle?.relatedArticles &&
    data?.newsArticle?.relatedArticles?.length > 0
  ) {
    if (
      !(
        data?.newsArticle?.relatedArticles.every((article) => {
          return (
            article?.title &&
            article.coverImage &&
            article.updatedDate &&
            article.timeToRead &&
            article.detailPage
          );
        }) && typeof newsArticle?.relatedArticlesTitle === "string"
      )
    ) {
      console.error(
        "Missing Related Articles data in News Article " + route.params.article
      );
      // return false;
    }
  }

  // check any image carousel data
  if (
    data?.newsArticle?.imageCarousel &&
    data?.newsArticle?.imageCarousel.length
  ) {
    if (
      !data?.newsArticle?.imageCarousel.every((imageItem) => {
        return imageItem?.image && imageItem?.imageAlt;
      })
    ) {
      console.error(
        "Missing Image Carousel data in News Article " + route.params.article
      );
      // return false;
    }
  }

  // check any image data
  if (
    (data?.newsArticle?.firstImage &&
      !isImageDataPresent(data?.newsArticle?.firstImage)) ||
    (data?.newsArticle?.secondImage &&
      !isImageDataPresent(data?.newsArticle?.secondImage)) ||
    (data?.newsArticle?.thirdImage &&
      !isImageDataPresent(data?.newsArticle?.thirdImage))
  ) {
    // debugger;
    console.error(
      "Missing First, Second, or Third Image data in News Article " +
        route.params.article
    );
    // return false;
  }

  // check any video data
  if (
    (data?.newsArticle?.firstVideo &&
      !isVideoDataPresent(data?.newsArticle?.firstVideo)) ||
    (data?.newsArticle?.secondVideo &&
      !isVideoDataPresent(data?.newsArticle?.secondVideo))
  ) {
    console.error(
      "Missing First or Second Video data in News Article " +
        route.params.article
    );
    // return false;
  }

  // check any card information data
  if (
    (data?.newsArticle?.firstCardInformation &&
      !isCardInfoPresent(data?.newsArticle?.firstCardInformation)) ||
    (data?.newsArticle?.secondCardInformation &&
      !isCardInfoPresent(data?.newsArticle?.secondCardInformation))
  ) {
    console.error(
      "Missing Card Information data in News Article " + route.params.article
    );
    // return false;
  }

  // check any infographic data
  if (
    (data?.newsArticle?.firstInfographic &&
      !isInfographicPresent(data?.newsArticle?.firstInfographic)) ||
    (data?.newsArticle?.secondInfographic &&
      !isInfographicPresent(data?.newsArticle?.secondInfographic))
  ) {
    console.error(
      "Missing Infographic data in News Article " + route.params.article
    );
    // return false;
  }

  // check any testimonial data
  if (data?.newsArticle?.firstTestimonial) {
    if (
      !(
        data?.newsArticle?.firstTestimonial.quote &&
        data?.newsArticle?.firstTestimonial.authorName &&
        data?.newsArticle?.firstTestimonial.authorDesignation
      )
    ) {
      console.error(
        "Missing First Testimonial data in News Article " + route.params.article
      );
      // return false;
    }
  }

  if (data?.newsArticle?.secondTestimonial) {
    if (
      !(
        data?.newsArticle?.secondTestimonial.quote &&
        data?.newsArticle?.secondTestimonial.authorName &&
        data?.newsArticle?.secondTestimonial.authorDesignation
      )
    ) {
      console.error(
        "Missing Second Testimonial data in News Article " +
          route.params.article
      );
      // return false;
    }
  }

  // check author data
  if (
    !(
      typeof newsArticle?.author?.name === "string" &&
      typeof newsArticle?.author?.bio === "string"
    )
  ) {
    console.error(
      "Missing author data in News Article " + route.params.article
    );
    // return false;
  }

  // all checked data present
  return true;
}

function isCardInfoPresent(cardInformation: ParagraphCardInformation) {
  let validLinks = true; // links may be empty

  // if links are present, ensure their values are too
  if (
    Array.isArray(cardInformation.htmlLinks) &&
    cardInformation.htmlLinks.length
  ) {
    validLinks = cardInformation.htmlLinks.every((link) => {
      return link?.uri && link?.target && link?.text;
    });
  }

  return (
    validLinks &&
    typeof cardInformation.image === "string" &&
    typeof cardInformation.title === "string"
  );
}

function isInfographicPresent(infographic: ParagraphInfographicAtlas) {
  return (
    typeof infographic.image === "string" &&
    typeof infographic.imageAlt === "string" &&
    typeof infographic.ctaText === "string"
  );
}

function isImageDataPresent(image: ParagraphImageApollo) {
  return (
    typeof image.image === "string" &&
    typeof image.imageAlt === "string" &&
    typeof image.isPortrait === "boolean"
  );
}

function isVideoDataPresent(video: ParagraphVideoApollo) {
  return (
    typeof video.youtubeId === "string" && typeof video.imageSource === "string"
  );
}
</script>

<style lang="scss" scoped>
.cia-div {
  width: 340px;
}
.cia-image {
  height: 200px;
  width: 100%;
  object-fit: cover;
  object-position: top;
}
.cia-line {
  height: 4px;
  width: 100%;
}
.social-media-icon {
  height: 32px;
  width: 32px;
}
.title-underline {
  width: 66px;
  height: 8px;
}
.line {
  width: 47px;
  height: 8px;
}
.bg-image {
  background-repeat: repeat-y;
  background-size: 100%;
}
.content-body-text {
  font-size: 18px;
  line-height: 29px;
}

@media (min-width: 768px) {
  .cia-div {
    width: 540px;
  }
  .cia-image {
    height: 350px;
  }
  .bg-image {
    background-size: auto 50%;
    background-repeat: repeat;
  }
}
</style>
